import React, { useState } from 'react'
import { FaTrash, FaPlus, FaCaretDown, FaStar, FaRegSmile, FaHashtag } from 'react-icons/fa'
import { LuSparkles } from 'react-icons/lu'
import { Heading, Text, Stack, Icon, Button, Input, DateChooser, TimeChooser, Checkbox, Box, Select, Radio, RadioGroup, Alert, Tag, SimpleTable, SimpleMenu, ColourChooser, Wrap, WrapItem, FormControl, CodeTextarea, SearchableSelect, Card } from '@/components/lib'
import ConfirmButton from '../includes/ConfirmButton'
import Integer from '../fields/Integer'
import Likert from '../fields/Likert'
import Rating from '../fields/Rating'
import Table from '../fields/Table'
import TableColumnsEditor, { MAX_TABLE_COLUMNS, MAX_TABLE_ROWS, TABLE_COLUMN_TYPES } from '../includes/TableColumnsEditor'
import utils, { newLikertStatementKey, newTableColumnId, relabelForPoints, ratingPoints, ratingLabels, ratingSymbol, DEFAULT_RATING_LABELS, DEFAULT_RATING_POINTS, DEFAULT_RATING_SYMBOL, MIN_RATING_POINTS, MAX_RATING_POINTS, RATING_SYMBOLS } from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

// Ready-made scales for the Likert matrix. These only pre-fill the editor — the
// scale that gets saved is whatever is in the table when the form is submitted.
// The `value` of each point is what gets stored in responses and summed for
// scoring, so the zero-based presets match how those instruments are scored.
export const likertPresets = [
  {
    key: 'agreement5',
    label: 'Agreement (5 point)',
    points: [
      { label: 'Strongly disagree', value: 1 },
      { label: 'Disagree', value: 2 },
      { label: 'Neither agree nor disagree', value: 3 },
      { label: 'Agree', value: 4 },
      { label: 'Strongly agree', value: 5 }
    ]
  },
  {
    key: 'agreement7',
    label: 'Agreement (7 point)',
    points: [
      { label: 'Strongly disagree', value: 1 },
      { label: 'Disagree', value: 2 },
      { label: 'Somewhat disagree', value: 3 },
      { label: 'Neither agree nor disagree', value: 4 },
      { label: 'Somewhat agree', value: 5 },
      { label: 'Agree', value: 6 },
      { label: 'Strongly agree', value: 7 }
    ]
  },
  {
    key: 'frequency4',
    label: 'Frequency (0-3, as scored by GAD-7 and PHQ-9)',
    points: [
      { label: 'Not at all', value: 0 },
      { label: 'Several days', value: 1 },
      { label: 'More than half the days', value: 2 },
      { label: 'Nearly every day', value: 3 }
    ]
  },
  {
    key: 'satisfaction5',
    label: 'Satisfaction (5 point)',
    points: [
      { label: 'Very dissatisfied', value: 1 },
      { label: 'Dissatisfied', value: 2 },
      { label: 'Neither satisfied nor dissatisfied', value: 3 },
      { label: 'Satisfied', value: 4 },
      { label: 'Very satisfied', value: 5 }
    ]
  },
  {
    key: 'change5',
    label: 'Change (bipolar, -2 to 2)',
    points: [
      { label: 'Much worse', value: -2 },
      { label: 'A little worse', value: -1 },
      { label: 'No change', value: 0 },
      { label: 'A little better', value: 1 },
      { label: 'Much better', value: 2 }
    ]
  }
]

export const times = [
  { key: '10', value: 10, text: '10 seconds' },
  { key: '30', value: 30, text: '30 seconds' },
  { key: '45', value: 45, text: '45 seconds' },
  { key: '60', value: 60, text: '1 minute' },
  { key: '120', value: 120, text: '2 minutes' }
]

export const nutritionTypes = [
  { key: 'carbohydrate', name: 'Carbohydrate', unit: 'g' },
  { key: 'fat', name: 'Fat', unit: 'g' },
  { key: 'saturatedFat', name: 'Saturated fat', unit: 'g' },
  { key: 'fibre', name: 'Fibre', unit: 'g' },
  { key: 'protein', name: 'Protein', unit: 'g' },
  { key: 'salt', name: 'Salt', unit: 'g' },
  { key: 'sugars', name: 'Sugars', unit: 'g' },
  { key: 'energyKcal', name: 'Energy (Kcal)', unit: 'Kcal' }
]

// The data types that have something to configure beyond their name. Anything
// not listed here renders no configuration block at all.
export const CONFIGURABLE_TYPES = [
  'text', 'paragraph', 'integer', 'float', 'bmi', 'slider', 'rating', 'likert', 'table',
  'choice', 'date', 'datetime', 'time', 'stroop', 'pal', 'ravlt', 'food',
  'calculation', 'promisForm', 'promisBattery'
]

// What a brand-new table starts as. One free-text column and ten rows is the
// smallest thing that is actually usable, and matches what the API gives a
// table created from a bulk CSV.
const DEFAULT_TABLE_MAXIMUM_ROWS = 10

function newTableColumns () {
  return [{ id: newTableColumnId(), name: '', type: 'text', isOptional: false }]
}

function moveItem (items, from, to) {
  const next = [...items]
  next.splice(to, 0, next.splice(from, 1)[0])
  return next
}

// Owns every setting that belongs to the data type itself rather than to the
// field that uses it. Both the data type editor and the stage's field editor
// drive the same block of settings, so the state and the payload it turns into
// live here rather than in either of them.
export function useDataTypeConfiguration () {
  const [textMin, setTextMin] = useState(0)
  const [textMax, setTextMax] = useState(0)
  const [minValue, setMinValue] = useState(null)
  const [maxValue, setMaxValue] = useState(null)
  const [sliderMin, setSliderMin] = useState(1)
  const [sliderMax, setSliderMax] = useState(10)
  const [likertScalePoints, setLikertScalePoints] = useState(likertPresets[0].points)
  const [likertStatementRows, setLikertStatementRows] = useState([])
  const [likertIncludeNotApplicable, setLikertIncludeNotApplicable] = useState(false)
  // Local only — lets the admin try the field out while building it
  const [likertPreview, setLikertPreview] = useState({})
  const [ratingPointCount, setRatingPointCount] = useState(DEFAULT_RATING_POINTS)
  const [ratingSymbolStyle, setRatingSymbolStyle] = useState(DEFAULT_RATING_SYMBOL)
  const [ratingLabelRows, setRatingLabelRows] = useState(() => [...DEFAULT_RATING_LABELS[DEFAULT_RATING_POINTS]])
  // Local only — lets the admin try the field out while building it
  const [ratingPreview, setRatingPreview] = useState(null)
  const [tableColumns, setTableColumns] = useState(newTableColumns)
  const [tableMaximumRows, setTableMaximumRows] = useState(DEFAULT_TABLE_MAXIMUM_ROWS)
  // Local only — lets the admin try the field out while building it
  const [tablePreview, setTablePreview] = useState(null)
  const [hideBmiIndication, setHideBmiIndication] = useState(false)
  const [dateMin, setDateMin] = useState()
  const [dateMax, setDateMax] = useState()
  const [timeMin, setTimeMin] = useState()
  const [timeMax, setTimeMax] = useState()
  const [choiceOptions, setChoiceOptions] = useState([])
  const [newChoiceOption, setNewChoiceOption] = useState('')
  const [choiceOptionError, setChoiceOptionError] = useState(null)
  const [choiceMultipleSelection, setChoiceMultipleSelection] = useState(false)
  const [choiceIncludeNone, setChoiceIncludeNone] = useState(false)
  const [choiceShowAsDropdown, setChoiceShowAsDropdown] = useState(false)
  const [stroopOptions, setStroopOptions] = useState([])
  const [newStroopOption, setNewStroopOption] = useState('')
  const [newStroopColour, setNewStroopColour] = useState('')
  const [stroopTimeLimit, setStroopTimeLimit] = useState(60)
  const [palWordPairs, setPalWordPairs] = useState([])
  const [palPairTimeout, setPalPairTimeout] = useState(3)
  const [palAnswerTimeout, setPalAnswerTimeout] = useState(180)
  const [palDisplayAnswerTimer, setPalDisplayAnswerTimer] = useState(true)
  const [palDisplayCorrectResult, setPalDisplayCorrectResult] = useState(true)
  const [ravltTestSet, setRavltTestSet] = useState(Array(15).fill(''))
  const [ravltInterruptionSet, setRavltInterruptionSet] = useState(Array(15).fill(''))
  const [ravltPreInterruptionTests, setRavltPreInterruptionTests] = useState(3)
  const [ravltPostInterruptionTests, setRavltPostInterruptionTests] = useState(1)
  const [foodSource, setFoodSource] = useState('openFoodFacts')
  const [requiredFoodFields, setRequiredFoodFields] = useState(['protein', 'carbohydrate', 'fat', 'energyKcal'])
  const [formula, setFormula] = useState('')
  const [formulaDescription, setFormulaDescription] = useState('')
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [selectedDataTypeForInsert, setSelectedDataTypeForInsert] = useState('')
  const [promisFormOid, setPromisFormOid] = useState('')
  const [promisBatteryOid, setPromisBatteryOid] = useState('')
  const [promisShowTScore, setPromisShowTScore] = useState(false)

  // Fill the editor in from a stored data type
  function load (dataType) {
    setHideBmiIndication(dataType.hideBmiIndication || false)
    setTextMin(dataType.minimumLength || 0)
    setTextMax(dataType.maximumLength || 0)
    setMinValue(dataType.minimumValue ?? null)
    setMaxValue(dataType.maximumValue ?? null)
    setSliderMin(dataType.minimum ?? 1)
    setSliderMax(dataType.maximum ?? 10)
    setLikertScalePoints(dataType.scalePoints?.length ? dataType.scalePoints : likertPresets[0].points)
    setLikertStatementRows(dataType.statements || [])
    setLikertIncludeNotApplicable(dataType.includeNotApplicable || false)
    // Read back through the same helpers the field itself uses, so a scale
    // whose labels no longer match its point count opens in the editor
    // showing exactly what a respondent would have been shown
    setRatingPointCount(ratingPoints(dataType))
    setRatingSymbolStyle(ratingSymbol(dataType))
    setRatingLabelRows([...ratingLabels(dataType)])
    setTableColumns(dataType.columns?.length ? dataType.columns : newTableColumns())
    setTableMaximumRows(dataType.maximumRows ?? DEFAULT_TABLE_MAXIMUM_ROWS)
    setChoiceOptions(dataType.options || [])
    setChoiceMultipleSelection(dataType.multiple || false)
    setChoiceIncludeNone(dataType.includeNone || false)
    setChoiceShowAsDropdown(dataType.showAsDropdown || false)
    setDateMin(dataType.earliestDate ? new Date(dataType.earliestDate) : null)
    setDateMax(dataType.latestDate ? new Date(dataType.latestDate) : null)
    setTimeMin(dataType.earliestTime || null)
    setTimeMax(dataType.latestTime || null)
    setStroopOptions(dataType.options || [])
    setStroopTimeLimit(dataType.timeLimit || 60)
    setPalWordPairs(dataType.wordPairs || [])
    setPalPairTimeout(dataType.pairTimeout || 3)
    setPalAnswerTimeout(dataType.answerTimeout ?? 180)
    setPalDisplayAnswerTimer(dataType.displayAnswerTimer)
    setPalDisplayCorrectResult(dataType.displayCorrectResult)
    setRavltTestSet(dataType.testSet || Array(15).fill(''))
    setRavltInterruptionSet(dataType.interruptionSet || Array(15).fill(''))
    setRavltPreInterruptionTests(dataType.preInterruptionTests || 3)
    setRavltPostInterruptionTests(dataType.postInterruptionTests || 1)
    setFoodSource(dataType.foodSource || 'openFoodFacts')
    setRequiredFoodFields(dataType.requiredFoodFields || ['protein', 'carbohydrate', 'fat', 'energyKcal'])
    setFormula(dataType.formula || '')
    setFormulaDescription(dataType.formulaDescription || '')
    setPromisFormOid(dataType.promisFormOid || '')
    setPromisBatteryOid(dataType.promisBatteryOid || '')
    setPromisShowTScore(dataType.promisShowTScore || false)
  }

  // Put the editor back to how a brand-new data type starts
  function reset () {
    setTextMin(0)
    setTextMax(0)
    setMinValue(null)
    setMaxValue(null)
    setSliderMin(1)
    setSliderMax(10)
    setLikertScalePoints(likertPresets[0].points)
    setLikertStatementRows([])
    setLikertIncludeNotApplicable(false)
    setTableColumns(newTableColumns())
    setTableMaximumRows(DEFAULT_TABLE_MAXIMUM_ROWS)
    setTablePreview(null)
    setRatingPointCount(DEFAULT_RATING_POINTS)
    setRatingSymbolStyle(DEFAULT_RATING_SYMBOL)
    setRatingLabelRows([...DEFAULT_RATING_LABELS[DEFAULT_RATING_POINTS]])
    setRatingPreview(null)
    setHideBmiIndication(false)
    setChoiceOptions([])
    setChoiceMultipleSelection(false)
    setChoiceIncludeNone(false)
    setChoiceShowAsDropdown(false)
    setDateMin(null)
    setDateMax(null)
    setTimeMin(null)
    setTimeMax(null)
    setStroopOptions([])
    setStroopTimeLimit(60)
    setPalWordPairs([])
    setPalPairTimeout(3)
    setPalAnswerTimeout(180)
    setPalDisplayAnswerTimer(true)
    setPalDisplayCorrectResult(true)
    setRavltTestSet(Array(15).fill(''))
    setRavltInterruptionSet(Array(15).fill(''))
    setRavltPreInterruptionTests(3)
    setRavltPostInterruptionTests(1)
    setFoodSource('openFoodFacts')
    setRequiredFoodFields(['protein', 'carbohydrate', 'fat', 'energyKcal'])
    setFormula('')
    setFormulaDescription('')
    setPromisFormOid('')
    setPromisBatteryOid('')
    setPromisShowTScore(false)
  }

  // Turn the current settings into the payload the API expects for `type`.
  // Returns `{ data }`, or `{ error }` with a message to show the admin.
  function build (type) {
    const data = {}
    if (['text', 'paragraph'].indexOf(type) > -1) {
      data.minimumLength = textMin || 0
      data.maximumLength = textMax || 0
      if (textMin < 0 || textMax < 0) return { error: 'Minimum and maximum values must be above 0' }
      if (textMin && textMax && textMax < textMin) return { error: 'Maximum text length cannot be lower than the minimum' }
    }
    if (['integer', 'float'].indexOf(type) > -1) {
      data.minimumValue = Number.isFinite(minValue) ? minValue : null
      data.maximumValue = Number.isFinite(maxValue) ? maxValue : null
      if (data.minimumValue !== null && data.maximumValue !== null && data.maximumValue < data.minimumValue) return { error: 'Maximum value cannot be lower than the minimum' }
    }
    if (type === 'slider') {
      data.minimum = sliderMin
      data.maximum = sliderMax
      if (data.minimum == null || data.maximum == null) return { error: 'You must choose a minimum and maximum slider value' }
    }
    if (type === 'likert') {
      if (likertScalePoints.length < 2) return { error: 'A Likert scale needs at least two points' }
      if (likertScalePoints.some(p => !p.label?.trim())) return { error: 'Every scale point needs a label' }
      if (likertScalePoints.some(p => !Number.isInteger(p.value))) return { error: 'Every scale point needs a whole-number value' }
      if (new Set(likertScalePoints.map(p => p.value)).size !== likertScalePoints.length) return { error: 'Each scale point must have a different value' }
      if (!likertStatementRows.length) return { error: 'You must provide at least one statement' }
      if (likertStatementRows.some(s => !s.label?.trim())) return { error: 'Every statement needs some text' }
      data.scalePoints = likertScalePoints.map(p => ({ label: p.label.trim(), value: p.value }))
      // The key travels with the statement so re-wording or reordering it keeps
      // the answers already collected against it
      data.statements = likertStatementRows.map(s => ({ key: s.key, label: s.label.trim() }))
      data.includeNotApplicable = likertIncludeNotApplicable
    }
    if (type === 'rating') {
      if (!Number.isInteger(ratingPointCount) || ratingPointCount < MIN_RATING_POINTS || ratingPointCount > MAX_RATING_POINTS) {
        return { error: `A rating scale can have between ${MIN_RATING_POINTS} and ${MAX_RATING_POINTS} points` }
      }
      if (!RATING_SYMBOLS.includes(ratingSymbolStyle)) return { error: 'Choose how the scale is drawn' }
      if (ratingLabelRows.length !== ratingPointCount) return { error: `A ${ratingPointCount}-point rating scale needs exactly ${ratingPointCount} labels` }
      if (ratingLabelRows.some(l => !l?.trim())) return { error: 'Every rating point needs a label' }
      // Labels are positional — nothing is stored against them — so re-wording
      // one re-labels the ratings already collected against that position
      data.points = ratingPointCount
      data.symbol = ratingSymbolStyle
      data.labels = ratingLabelRows.map(l => l.trim())
    }
    if (type === 'table') {
      if (!tableColumns.length) return { error: 'You must provide at least one column' }
      if (tableColumns.length > MAX_TABLE_COLUMNS) return { error: `A table can have at most ${MAX_TABLE_COLUMNS} columns` }
      if (tableColumns.some(c => !c.name?.trim())) return { error: 'Every column needs a name' }
      const names = tableColumns.map(c => c.name.trim().toLowerCase())
      if (new Set(names).size !== names.length) return { error: 'Each column must have a different name' }
      if (tableColumns.some(c => !TABLE_COLUMN_TYPES.some(t => t.key === c.type))) return { error: 'Every column needs a format' }
      if (!Number.isInteger(tableMaximumRows) || tableMaximumRows < 1 || tableMaximumRows > MAX_TABLE_ROWS) {
        return { error: `A table can allow between 1 and ${MAX_TABLE_ROWS} rows` }
      }
      // The id travels with the column so renaming or reordering it keeps the
      // cells already collected against it
      data.columns = tableColumns.map(c => ({ id: c.id, name: c.name.trim(), type: c.type, isOptional: !!c.isOptional }))
      data.maximumRows = tableMaximumRows
    }
    if (type === 'choice') {
      data.options = choiceOptions
      data.multiple = choiceMultipleSelection
      data.includeNone = choiceIncludeNone
      data.showAsDropdown = !choiceMultipleSelection && choiceShowAsDropdown
      if (!data.options || !data.options.length) return { error: 'You must provide at least one option' }
    }
    if (['date', 'datetime'].indexOf(type) > -1) {
      data.earliestDate = dateMin || null
      data.latestDate = dateMax || null
      if (dateMin && dateMax && dateMax < dateMin) return { error: 'Latest date cannot be earlier than the earliest date' }
    }
    if (type === 'time') {
      data.earliestTime = timeMin || null
      data.latestTime = timeMax || null
      if (timeMin && timeMax && timeMax < timeMin) return { error: 'Latest time cannot be earlier than the earliest time' }
    }
    if (type === 'stroop') {
      data.options = stroopOptions
      data.timeLimit = stroopTimeLimit
      if (!data.options || !data.options.length) return { error: 'You must provide at least one colour test' }
    }
    if (type === 'pal') {
      data.wordPairs = palWordPairs
      data.pairTimeout = palPairTimeout
      data.answerTimeout = palAnswerTimeout || 0
      data.displayAnswerTimer = palDisplayAnswerTimer
      data.displayCorrectResult = palDisplayCorrectResult
      if (!data.wordPairs || !data.wordPairs.length) return { error: 'You must provide at least one word pair' }
    }
    if (type === 'ravlt') {
      data.testSet = ravltTestSet
      data.interruptionSet = ravltInterruptionSet
      data.preInterruptionTests = ravltPreInterruptionTests
      data.postInterruptionTests = ravltPostInterruptionTests
    }
    if (type === 'food') {
      data.foodSource = foodSource
      data.requiredFoodFields = requiredFoodFields
    }
    if (type === 'bmi') {
      data.hideBmiIndication = hideBmiIndication
    }
    if (type === 'calculation') {
      data.formula = formula?.trim() || null
    }
    if (type === 'promisForm') {
      if (!promisFormOid) return { error: 'Please select a PROMIS form' }
      data.promisFormOid = promisFormOid
      data.promisShowTScore = promisShowTScore
    }
    if (type === 'promisBattery') {
      if (!promisBatteryOid) return { error: 'Please select a PROMIS battery' }
      data.promisBatteryOid = promisBatteryOid
      data.promisShowTScore = promisShowTScore
    }
    return { data }
  }

  return {
    textMin,
    setTextMin,
    textMax,
    setTextMax,
    minValue,
    setMinValue,
    maxValue,
    setMaxValue,
    sliderMin,
    setSliderMin,
    sliderMax,
    setSliderMax,
    likertScalePoints,
    setLikertScalePoints,
    likertStatementRows,
    setLikertStatementRows,
    likertIncludeNotApplicable,
    setLikertIncludeNotApplicable,
    likertPreview,
    setLikertPreview,
    ratingPointCount,
    setRatingPointCount,
    ratingSymbolStyle,
    setRatingSymbolStyle,
    ratingLabelRows,
    setRatingLabelRows,
    ratingPreview,
    setRatingPreview,
    tableColumns,
    setTableColumns,
    tableMaximumRows,
    setTableMaximumRows,
    tablePreview,
    setTablePreview,
    hideBmiIndication,
    setHideBmiIndication,
    dateMin,
    setDateMin,
    dateMax,
    setDateMax,
    timeMin,
    setTimeMin,
    timeMax,
    setTimeMax,
    choiceOptions,
    setChoiceOptions,
    newChoiceOption,
    setNewChoiceOption,
    choiceOptionError,
    setChoiceOptionError,
    choiceMultipleSelection,
    setChoiceMultipleSelection,
    choiceIncludeNone,
    setChoiceIncludeNone,
    choiceShowAsDropdown,
    setChoiceShowAsDropdown,
    stroopOptions,
    setStroopOptions,
    newStroopOption,
    setNewStroopOption,
    newStroopColour,
    setNewStroopColour,
    stroopTimeLimit,
    setStroopTimeLimit,
    palWordPairs,
    setPalWordPairs,
    palPairTimeout,
    setPalPairTimeout,
    palAnswerTimeout,
    setPalAnswerTimeout,
    palDisplayAnswerTimer,
    setPalDisplayAnswerTimer,
    palDisplayCorrectResult,
    setPalDisplayCorrectResult,
    ravltTestSet,
    setRavltTestSet,
    ravltInterruptionSet,
    setRavltInterruptionSet,
    ravltPreInterruptionTests,
    setRavltPreInterruptionTests,
    ravltPostInterruptionTests,
    setRavltPostInterruptionTests,
    foodSource,
    setFoodSource,
    requiredFoodFields,
    setRequiredFoodFields,
    formula,
    setFormula,
    formulaDescription,
    setFormulaDescription,
    generatingDescription,
    setGeneratingDescription,
    selectedDataTypeForInsert,
    setSelectedDataTypeForInsert,
    promisFormOid,
    setPromisFormOid,
    promisBatteryOid,
    setPromisBatteryOid,
    promisShowTScore,
    setPromisShowTScore,
    load,
    reset,
    build
  }
}

// Renders the settings that belong to the selected data type format. A format
// with nothing to configure renders nothing at all, so a caller can drop this
// in unconditionally.
export default function DataTypeConfiguration ({ config, selectedType, isEditing, dataType, headingPrefix = '', saveDataType }) {
  const { addDataType, dataTypes } = useStore()
  const {
    textMin, setTextMin, textMax, setTextMax, minValue, setMinValue, maxValue, setMaxValue, sliderMin, setSliderMin,
    sliderMax, setSliderMax, likertScalePoints, setLikertScalePoints, likertStatementRows, setLikertStatementRows,
    likertIncludeNotApplicable, setLikertIncludeNotApplicable, likertPreview, setLikertPreview, ratingPointCount, setRatingPointCount, ratingSymbolStyle,
    setRatingSymbolStyle, ratingLabelRows, setRatingLabelRows, ratingPreview, setRatingPreview, tableColumns,
    setTableColumns, tableMaximumRows, setTableMaximumRows, tablePreview, setTablePreview, hideBmiIndication,
    setHideBmiIndication, dateMin, setDateMin, dateMax, setDateMax, timeMin, setTimeMin, timeMax, setTimeMax,
    choiceOptions, setChoiceOptions, newChoiceOption, setNewChoiceOption, choiceOptionError, setChoiceOptionError,
    choiceMultipleSelection, setChoiceMultipleSelection, choiceIncludeNone, setChoiceIncludeNone,
    choiceShowAsDropdown, setChoiceShowAsDropdown, stroopOptions, setStroopOptions, newStroopOption,
    setNewStroopOption, newStroopColour, setNewStroopColour, stroopTimeLimit, setStroopTimeLimit, palWordPairs,
    setPalWordPairs, palPairTimeout, setPalPairTimeout, palAnswerTimeout, setPalAnswerTimeout, palDisplayAnswerTimer,
    setPalDisplayAnswerTimer, palDisplayCorrectResult, setPalDisplayCorrectResult, ravltTestSet, setRavltTestSet,
    ravltInterruptionSet, setRavltInterruptionSet, ravltPreInterruptionTests, setRavltPreInterruptionTests,
    ravltPostInterruptionTests, setRavltPostInterruptionTests, foodSource, setFoodSource, requiredFoodFields,
    setRequiredFoodFields, formula, setFormula, formulaDescription, setFormulaDescription, generatingDescription,
    setGeneratingDescription, selectedDataTypeForInsert, setSelectedDataTypeForInsert, promisFormOid,
    promisBatteryOid, promisShowTScore, setPromisShowTScore
  } = config

  function addStroopOption () {
    if (!newStroopColour || !newStroopOption) return
    setStroopOptions([...stroopOptions, { colour: newStroopColour, text: newStroopOption }])
    setNewStroopOption('')
    setNewStroopColour('')
  }
  function removeStroopOption (index) {
    setStroopOptions(stroopOptions.filter((o, i) => i !== index))
  }

  function addPalWordPair () {
    setPalWordPairs([...palWordPairs, { words: ['', ''], related: false }])
  }
  function removePalWordPair (index) {
    setPalWordPairs(palWordPairs.filter((pair, i) => i !== index))
  }
  function updatePalWordPairRelated (index, related) {
    setPalWordPairs(palWordPairs.map((pair, i) => i === index ? { ...pair, related } : pair))
  }
  function updatePalWordPair (index, pairIndex, newWord) {
    setPalWordPairs(palWordPairs.map((pair, i) =>
      i === index ? { ...pair, words: pair.words.map((w, j) => j === pairIndex ? newWord : w) } : pair))
  }

  function addChoiceOption () {
    const option = newChoiceOption.trim()
    if (!option) return
    if (choiceOptions.includes(option)) return setChoiceOptionError('That option has already been added.')
    setChoiceOptionError(null)
    setChoiceOptions([...choiceOptions, option])
    setNewChoiceOption('')
  }
  function removeChoiceOption (i) {
    setChoiceOptions(choiceOptions.filter((o, index) => index !== i))
  }

  // The preview stands in for a blank label rather than letting the field fall
  // back for the whole scale, which is what it does with a stored one: clearing
  // a single box mid-edit would otherwise swap every label on screen for a
  // default, and it isn't obvious that only one of them was the problem.
  const ratingPreviewLabels = ratingLabelRows.map((label, index) =>
    label?.trim() || (DEFAULT_RATING_LABELS[ratingPointCount] ?? [])[index] || `Point ${index + 1}`)

  if (CONFIGURABLE_TYPES.indexOf(selectedType) === -1) return null

  return (
    <>
      {['text', 'paragraph'].indexOf(selectedType) > -1 &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure this data type</Heading>
          <Text>Choose extra settings for this field.</Text>
          <Text>Minimum allowed text length <small>(Leave as 0 or blank for no minimum)</small></Text>
          <Input placeholder='1' type='number' value={textMin} onChange={e => setTextMin(parseInt(e.target.value))} />
          <Text>Maximum allowed text length <small>(Leave as 0 or blank for no maximum)</small></Text>
          <Input placeholder='1' type='number' value={textMax} onChange={e => setTextMax(parseInt(e.target.value))} />
        </Card>}

      {['integer', 'float'].indexOf(selectedType) > -1 &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure this data type</Heading>
          <Text>Choose extra settings for this field.</Text>
          <Text>Minimum allowed value <small>(Leave blank for no minimum)</small></Text>
          <Stack direction='row' gap={2} alignItems='center'>
            <Box flex='1'><Integer value={minValue} onChange={setMinValue} /></Box>
            <Button
              variant='outline' size='sm' disabled={minValue == null}
              onClick={() => setMinValue(null)}
            >Remove minimum
            </Button>
          </Stack>
          <Text>Maximum allowed value <small>(Leave blank for no maximum)</small></Text>
          <Stack direction='row' gap={2} alignItems='center'>
            <Box flex='1'><Integer value={maxValue} onChange={setMaxValue} /></Box>
            <Button
              variant='outline' size='sm' disabled={maxValue == null}
              onClick={() => setMaxValue(null)}
            >Remove maximum
            </Button>
          </Stack>
        </Card>}

      {selectedType === 'bmi' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}BMI settings</Heading>
          <FormControl helperText='The BMI is still calculated and stored either way. This only controls whether the figure, dial and category are shown on the field itself.'>
            <Checkbox isChecked={hideBmiIndication || false} onChange={e => setHideBmiIndication(e.checked)}>
              Hide the BMI indication from this field (not available on mobile)
            </Checkbox>
          </FormControl>
        </Card>}

      {selectedType === 'slider' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Slider settings</Heading>
          <Text>Allow participants to select a number between the following minimum and maximum.</Text>
          <Text>Minimum value</Text>
          <Integer value={sliderMin} onChange={setSliderMin} />
          <Text>Maximum value</Text>
          <Integer value={sliderMax} onChange={setSliderMax} />
        </Card>}

      {selectedType === 'rating' &&
        <Card gap={5}>
          <Heading as='h3' size='md'>{headingPrefix}Rating settings</Heading>
          <Text>Respondents rate one thing on a single scale, representing an integer.</Text>

          {isEditing &&
            <Alert status='warning' variant='left-accent' title='Changing the scale once responses exist'>
              Please note that reducing the number of points may cause issues in responses already submitted.
            </Alert>}

          <Stack gap={3}>
            <Stack direction={{ base: 'column', md: 'row' }} gap={4} alignItems={{ md: 'center' }}>
              <Text fontSize='sm'>Scale points</Text>
              <Box w='120px'>
                <Select
                  size='sm'
                  value={String(ratingPointCount)}
                  onChange={e => {
                    const points = parseInt(e.target.value, 10)
                    setRatingPointCount(points)
                    setRatingLabelRows(relabelForPoints(ratingLabelRows, points))
                    // A preview rating the shortened scale can no longer
                    // show. The field itself leaves such a value alone
                    // rather than reporting it, so clear it here.
                    if (ratingPreview !== null && ratingPreview > points) setRatingPreview(null)
                  }}
                >
                  {Array.from({ length: MAX_RATING_POINTS - MIN_RATING_POINTS + 1 }, (_, i) => MIN_RATING_POINTS + i)
                    .map(points => <option key={points} value={points}>{points} points</option>)}
                </Select>
              </Box>
            </Stack>

            <Text fontSize='sm'>Scale symbols</Text>
            <RadioGroup value={ratingSymbolStyle} onValueChange={e => setRatingSymbolStyle(e.value)} colorPalette='primary'>
              <Stack direction={{ base: 'column', md: 'row' }} gap={{ base: 2, md: 6 }}>
                <Radio value='stars'><Stack direction='row' gap={2} alignItems='center'><Icon as={FaStar} /><Text fontSize='sm'>Stars</Text></Stack></Radio>
                <Radio value='numeric'><Stack direction='row' gap={2} alignItems='center'><Icon as={FaHashtag} /><Text fontSize='sm'>Numbers</Text></Stack></Radio>
                <Radio value='smileys'><Stack direction='row' gap={2} alignItems='center'><Icon as={FaRegSmile} /><Text fontSize='sm'>Smileys</Text></Stack></Radio>
              </Stack>
            </RadioGroup>
          </Stack>

          <Stack gap={3}>
            <Heading as='h4' size='sm'>Text labels</Heading>
            <Stack gap={2}>
              {ratingLabelRows.map((label, index) =>
                <Stack direction='row' gap={2} key={index} alignItems='center'>
                  <Text fontSize='sm' color='fg.muted' w='2rem' textAlign='right'>{index + 1}</Text>
                  <Input
                    size='sm'
                    placeholder={`Label for ${index + 1}, e.g. ${(DEFAULT_RATING_LABELS[ratingPointCount] ?? [])[index] ?? 'Good'}`}
                    value={label}
                    onChange={e => setRatingLabelRows(ratingLabelRows.map((l, i) => i === index ? e.target.value : l))}
                  />
                </Stack>
              )}
            </Stack>
            <Box>
              <Button
                variant='outline' size='sm'
                onClick={() => setRatingLabelRows([...(DEFAULT_RATING_LABELS[ratingPointCount] ?? DEFAULT_RATING_LABELS[DEFAULT_RATING_POINTS])])}
              >Use the default labels
              </Button>
            </Box>
          </Stack>

          <Box>
            <Heading as='h4' size='sm' mb={2}>Preview</Heading>
            <Rating
              dataType={{ points: ratingPointCount, symbol: ratingSymbolStyle, labels: ratingPreviewLabels }}
              value={ratingPreview}
              onChange={setRatingPreview}
            />
          </Box>
        </Card>}

      {selectedType === 'likert' &&
        <Card gap={5}>
          <Heading as='h3' size='md'>{headingPrefix}Likert settings</Heading>
          <Text>Respondents rate each of your statements on one shared scale.</Text>

          <Stack gap={3}>
            <Heading as='h4' size='sm'>The scale</Heading>
            <Box>
              <SimpleMenu
                trigger={<Button variant='outline' size='sm'>Select a ready-made scale... <Icon as={FaCaretDown} ml={1} /></Button>}
                items={likertPresets.map(preset => ({
                  type: 'item',
                  value: preset.key,
                  text: preset.label,
                  onClick: () => setLikertScalePoints(preset.points.map(p => ({ ...p })))
                }))}
              />
            </Box>

            <Text fontSize='sm' color='fg.muted'>
              If relevant, we recommend matching scores against whatever the instrument you are using expects (e.g. 0-3, 1-5, -2 to 2, etc.).
            </Text>
            <Stack gap={2}>
              {likertScalePoints.map((point, index) =>
                <Stack direction='row' gap={2} key={index} alignItems='center'>
                  <Input
                    placeholder='Label, e.g. Strongly agree'
                    value={point.label}
                    onChange={e => setLikertScalePoints(likertScalePoints.map((p, i) => i === index ? { ...p, label: e.target.value } : p))}
                  />
                  <Box w='120px'>
                    <Integer
                      value={point.value}
                      onChange={val => setLikertScalePoints(likertScalePoints.map((p, i) => i === index ? { ...p, value: val } : p))}
                    />
                  </Box>
                  <Button
                    variant='ghost' size='sm' aria-label='Remove scale point'
                    disabled={likertScalePoints.length <= 2}
                    onClick={() => setLikertScalePoints(likertScalePoints.filter((p, i) => i !== index))}
                  ><FaTrash />
                  </Button>
                </Stack>
              )}
            </Stack>
            <Box>
              <Button
                variant='outline' size='sm'
                disabled={likertScalePoints.length >= 11}
                onClick={() => setLikertScalePoints([...likertScalePoints, { label: '', value: (Math.max(...likertScalePoints.map(p => p.value)) || 0) + 1 }])}
              ><FaPlus /> Add a scale point
              </Button>
            </Box>
          </Stack>

          <Stack gap={3}>
            <Heading as='h4' size='sm'>The statements</Heading>
            <Stack gap={2}>
              {likertStatementRows.map((statement, index) =>
                <Stack direction='row' gap={2} key={statement.key} alignItems='center'>
                  <Input
                    placeholder='Statement, e.g. I slept well last night'
                    value={statement.label}
                    onChange={e => setLikertStatementRows(likertStatementRows.map((st, i) => i === index ? { ...st, label: e.target.value } : st))}
                  />
                  <Button
                    variant='outline' size='sm' aria-label='Move statement up' disabled={index === 0}
                    onClick={() => setLikertStatementRows(moveItem(likertStatementRows, index, index - 1))}
                  >↑
                  </Button>
                  <Button
                    variant='outline' size='sm' aria-label='Move statement down' disabled={index === likertStatementRows.length - 1}
                    onClick={() => setLikertStatementRows(moveItem(likertStatementRows, index, index + 1))}
                  >↓
                  </Button>
                  <Button variant='ghost' size='sm' aria-label='Remove statement' onClick={() => setLikertStatementRows(likertStatementRows.filter((st, i) => i !== index))}><FaTrash /></Button>
                </Stack>
              )}
            </Stack>
            {!likertStatementRows.length && <Text fontSize='sm' color='fg.muted'>No statements yet — add at least one.</Text>}
            <Box>
              <Button
                variant='outline' size='sm'
                disabled={likertStatementRows.length >= 30}
                onClick={() => setLikertStatementRows([...likertStatementRows, { key: newLikertStatementKey(), label: '' }])}
              ><FaPlus /> Add a statement
              </Button>
            </Box>
          </Stack>

          <Box>
            <Checkbox isChecked={likertIncludeNotApplicable} onChange={e => setLikertIncludeNotApplicable(e.checked)}>
              Include a "Not applicable" option on each row
            </Checkbox>
          </Box>

          {!!likertStatementRows.length && likertScalePoints.length >= 2 &&
            <Box>
              <Heading as='h4' size='sm' mb={2}>Preview</Heading>
              <Likert
                dataType={{ scalePoints: likertScalePoints, statements: likertStatementRows, includeNotApplicable: likertIncludeNotApplicable }}
                value={likertPreview}
                onChange={setLikertPreview}
              />
            </Box>}
        </Card>}

      {selectedType === 'table' &&
        <Card gap={5}>
          <Heading as='h3' size='md'>{headingPrefix}Table settings</Heading>
          <Text>Respondents fill in rows against the columns you define. A required table needs at least one complete row.</Text>

          <TableColumnsEditor
            columns={tableColumns}
            setColumns={setTableColumns}
            maximumRows={tableMaximumRows}
            setMaximumRows={setTableMaximumRows}
            isEditing={isEditing}
          />

          {tableColumns.some(c => c.name?.trim()) &&
            <Box>
              <Heading as='h4' size='sm' mb={2}>Preview</Heading>
              <Table
                dataType={{ columns: tableColumns, maximumRows: tableMaximumRows || 1 }}
                value={tablePreview}
                onChange={setTablePreview}
              />
            </Box>}
        </Card>}

      {selectedType === 'choice' &&
        <Card gap={3}>
          <Heading as='h4' size='md'>{headingPrefix}Choice settings</Heading>
          <Text>Manage the options for participants to select from.</Text>

          <Text>New option</Text>
          <Stack direction='row'>
            <Input
              value={newChoiceOption}
              onChange={e => { setNewChoiceOption(e.target.value); setChoiceOptionError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChoiceOption() } }}
            />
            <Button onClick={addChoiceOption}>Add option</Button>
          </Stack>
          {choiceOptionError && <Text fontSize='sm' color='fg.error'>{choiceOptionError}</Text>}
          <Stack gap={2}>
            {choiceOptions.map((c, index) =>
              <Stack direction='row' gap={2} key={c} alignItems='center' bg='bg.subtle' borderWidth='1px' rounded='md' px={3} py={1}>
                <Text flex={1}>{c}</Text>
                <Button
                  variant='outline' size='sm' aria-label='Move option up' disabled={index === 0}
                  onClick={() => setChoiceOptions(moveItem(choiceOptions, index, index - 1))}
                >↑
                </Button>
                <Button
                  variant='outline' size='sm' aria-label='Move option down' disabled={index === choiceOptions.length - 1}
                  onClick={() => setChoiceOptions(moveItem(choiceOptions, index, index + 1))}
                >↓
                </Button>
                <Button variant='ghost' size='sm' aria-label='Remove option' onClick={() => removeChoiceOption(index)}><FaTrash /></Button>
              </Stack>
            )}
          </Stack>
          {!choiceOptions.length && <Text fontSize='sm' color='fg.muted'>Please add at least one option.</Text>}
          <Box>
            <Checkbox isChecked={choiceMultipleSelection} onChange={e => setChoiceMultipleSelection(e.checked)}>Allow participants to select more than one option</Checkbox>
          </Box>
          {!choiceMultipleSelection &&
            <Box>
              <Checkbox isChecked={choiceShowAsDropdown} onChange={e => setChoiceShowAsDropdown(e.checked)}>Show as dropdown <small>(not available on mobile)</small></Checkbox>
            </Box>}
          <Box>
            <Checkbox isChecked={choiceIncludeNone} onChange={e => setChoiceIncludeNone(e.checked)}>Include an additional "None" option <small>(not available on mobile)</small></Checkbox>
          </Box>
        </Card>}

      {['date', 'datetime'].indexOf(selectedType) > -1 &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Date settings</Heading>
          <Text>Define the earliest and latest date available for this data type. Leave blank to leave the field as unrestricted.</Text>
          <Text>Earliest available date</Text>
          <Box maxW='300px' display='flex'>
            <DateChooser value={dateMin} onChange={setDateMin} />
            <Button ml={5} onClick={() => setDateMin(null)}>Unset</Button>
          </Box>

          <Text>Latest available date</Text>
          <Box maxW='300px' display='flex'>
            <DateChooser value={dateMax} onChange={setDateMax} />
            <Button ml={5} onClick={() => setDateMax(null)}>Unset</Button>
          </Box>
        </Card>}

      {selectedType === 'time' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Time settings</Heading>
          <Text>Define the earliest and latest time available for this data type. Leave blank to leave the field as unrestricted.</Text>
          <Text>Earliest available time</Text>
          <Box maxW='300px' display='flex'>
            <TimeChooser value={timeMin} onChange={setTimeMin} />
            <Button ml={5} onClick={() => setTimeMin(null)}>Unset</Button>
          </Box>

          <Text>Latest available time</Text>
          <Box maxW='300px' display='flex'>
            <TimeChooser value={timeMax} onChange={setTimeMax} />
            <Button ml={5} onClick={() => setTimeMax(null)}>Unset</Button>
          </Box>
        </Card>}

      {selectedType === 'stroop' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure this Stroop test</Heading>
          <Text>In the Stroop test, participants will be shown a series of "colour tests" (the name of a colour written in another colour), and will be asked to select the name of the colour the colour test is written in.</Text>
          <Text>Add text and colour combinations below for the Stroop test. We recommend choosing colours that are clearly legible on a white background.</Text>

          <Stack direction='row' alignItems='end'>
            <Box>
              <Text>Text colour</Text>
              <ColourChooser value={newStroopColour} onChange={setNewStroopColour} />
            </Box>
            <Box>
              <Text>Colour name</Text>
              <Input value={newStroopOption} onChange={e => setNewStroopOption(e.target.value)} placeholder='RED' />
            </Box>
            <Button onClick={addStroopOption}>Add option</Button>
          </Stack>

          <Wrap>
            {stroopOptions && stroopOptions.length > 0 &&
              <>
                {stroopOptions.map((o, i) =>
                  <WrapItem key={o.text}>
                    <Tag
                      size='lg' variant='subtle' mr={2}
                      label={o.text}
                      onClose={() => removeStroopOption(i)}
                    />
                  </WrapItem>
                )}
              </>}
          </Wrap>
          <Stack>
            <Text>How long should the test keep generating new combinations for?</Text>
            <Select value={stroopTimeLimit} onChange={e => setStroopTimeLimit(e.target.value)}>
              {times.map(o => <option key={o.key} value={o.value}>{o.text}</option>)}
            </Select>
          </Stack>
        </Card>}

      {selectedType === 'pal' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure this PAL test</Heading>
          <Text>In the PAL test, participants will be shown a series of word pair combinations, and are then asked to remember the correct pair for each given word.</Text>
          <Text>Add word pairs below for the PAL test.</Text>

          <Box display={{ md: 'flex' }}>
            {palWordPairs.length > 0 &&
              <SimpleTable
                headers={['Left word', 'Right word', 'Related', '']} rows={palWordPairs.map((p, i) => [
                  <Input key={`${i}-0`} value={p.words[0]} onChange={e => updatePalWordPair(i, 0, e.target.value)} />,
                  <Input key={`${i}-1`} value={p.words[1]} onChange={e => updatePalWordPair(i, 1, e.target.value)} />,
                  <Checkbox key={`${i}-r`} isChecked={p.related} onChange={e => updatePalWordPairRelated(i, e.checked)} />,
                  <Button key={`${i}-d`} variant='ghost' onClick={e => removePalWordPair(i)}><Icon><FaTrash /></Icon></Button>
                ])}
              />}
          </Box>
          <Button onClick={addPalWordPair}>Add word pair</Button>

          <Box>
            <Text>For how many seconds should participants be shown each pair?</Text>
            <Input type='number' value={palPairTimeout} onChange={e => setPalPairTimeout(parseInt(e.target.value) || '')} w='100px' />
            <Text>For how many seconds should participants be allowed to select matches for? (Leave 0 or blank for no limit)</Text>
            <Input type='number' placeholder='0' value={palAnswerTimeout} onChange={e => setPalAnswerTimeout(parseInt(e.target.value) || '')} w='100px' />
            <Text>Should the participant see a timer counting down when selecting pairs?</Text>
            <Checkbox isChecked={palDisplayAnswerTimer} onChange={e => setPalDisplayAnswerTimer(e.checked)}>Display answer timer</Checkbox>
            <Text>Should the participant see if they selected the correct pair after selecting?</Text>
            <Checkbox isChecked={palDisplayCorrectResult} onChange={e => setPalDisplayCorrectResult(e.checked)}>Display correct result</Checkbox>
          </Box>
        </Card>}

      {selectedType === 'ravlt' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure this RAVLT test</Heading>
          <Text>In the Rey Auditory Verbal Learning Test (RAVLT), participants will be shown or told a series of words and asked to recall them after a delay.</Text>
          <Alert status='info' variant='left-accent'>Please note that this test must be conducted by an administrator who fills-in the results on the participant page.</Alert>
          <Text>Provide two sets of 15 words. The first set is the "test" set and the second set is the "interruption" set.</Text>

          <Stack direction='row' spacing={5}>
            <Box>
              <Text>Number of pre-interruption tests</Text>
              <Select value={ravltPreInterruptionTests} onChange={e => setRavltPreInterruptionTests(e.target.value)}>
                {[1, 2, 3, 4, 5].map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
            </Box>
            <Box>
              <Text>Number of post-interruption tests</Text>
              <Select value={ravltPostInterruptionTests} onChange={e => setRavltPostInterruptionTests(e.target.value)}>
                {[1, 2, 3, 4, 5].map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
            </Box>
          </Stack>

          <SimpleTable
            headers={['Test set', 'Interruption set']} rows={ravltTestSet.map((w, i) => [
              <Input key={`${i}-test`} placeholder={`Test word ${i + 1}`} value={ravltTestSet[i]} onChange={e => setRavltTestSet(ravltTestSet.map((w, j) => i === j ? e.target.value : w))} />,
              <Input key={`${i}-interrupt`} placeholder={`Interruption word ${i + 1}`} value={ravltInterruptionSet[i]} onChange={e => setRavltInterruptionSet(ravltInterruptionSet.map((w, j) => i === j ? e.target.value : w))} />
            ])}
          />
        </Card>}

      {selectedType === 'food' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Configure the food input type</Heading>

          <Heading size='sm'>Nutrition data source</Heading>
          <Text>Trialflare supports different food nutrition sources for this type of data.</Text>
          <Select placeholder='Choose a food nutrition data source...' value={foodSource} onChange={e => setFoodSource(e.target.value)}>
            <option value='openFoodFacts'>Open Food Facts</option>
            <option value='nutritics'>Nutritics</option>
          </Select>

          <Heading size='sm'>Nutritional information required</Heading>
          <Text>Trialflare will try to automatically determine nutritional values from the data source. However, if these are unavailable for a particular item, you can specify the fields required to be completed manually.</Text>
          {nutritionTypes.map(n =>
            <Box key={n.key} p={2} display='inline-block'>
              <Checkbox
                key={n.key} isChecked={requiredFoodFields.indexOf(n.key) > -1}
                onChange={e => {
                  const index = requiredFoodFields.indexOf(n.key)
                  const newRequiredFoodFields = Object.assign([], requiredFoodFields)
                  if (index === -1) newRequiredFoodFields.push(n.key)
                  else newRequiredFoodFields.splice(index, 1)
                  setRequiredFoodFields(newRequiredFoodFields)
                }}
              >
                {n.name}
              </Checkbox>
            </Box>
          )}
        </Card>}

      {selectedType === 'promisForm' && promisFormOid &&
        <Stack gap={3} rounded='md' p={5} borderWidth='2px' bg='bg.surface'>
          <Heading as='h3' size='md'>{headingPrefix}PROMIS form settings</Heading>
          <Text>Configure how the assessment is presented to participants.</Text>
          <Checkbox isChecked={promisShowTScore} onChange={e => setPromisShowTScore(e.checked)}>
            Show T-Score to participant on completion
          </Checkbox>
        </Stack>}

      {selectedType === 'promisBattery' && promisBatteryOid &&
        <Stack gap={3} rounded='md' p={5} borderWidth='2px' bg='bg.surface'>
          <Heading as='h3' size='md'>{headingPrefix}PROMIS battery settings</Heading>
          <Text>Configure how the battery is presented to participants. Each form in the battery is administered in turn.</Text>
          {isEditing && dataType?.promisProprEligible != null &&
            <Alert status={dataType.promisProprEligible ? 'success' : 'info'} fontSize='sm'>
              {dataType.promisProprEligible
                ? 'This battery contains all seven PROPr domains, so a combined PROPr score will be calculated on completion.'
                : 'This battery does not contain all seven PROPr domains, so no combined PROPr score is calculated. Individual form scores are still recorded.'}
            </Alert>}
          <Checkbox isChecked={promisShowTScore} onChange={e => setPromisShowTScore(e.checked)}>
            Show T-Scores to participant on completion
          </Checkbox>
        </Stack>}

      {selectedType === 'calculation' &&
        <Card gap={3}>
          <Heading as='h3' size='md'>{headingPrefix}Calculation formula</Heading>
          <Text mb={3}>Write your formula using basic Python syntax. Use <code>{'{{DataTypeName}}'}</code> for values from other named data types.</Text>
          <FormControl label='Formula'>
            <CodeTextarea
              value={formula ?? ''}
              onChange={e => setFormula(e.target.value)}
              placeholder='e.g. {{Weight}} / (({{Height}}/100)**2)'
            />
          </FormControl>

          <Stack direction='row' alignItems='center' gap={3}>
            {dataTypes?.length > 0 &&
              <SearchableSelect
                options={dataTypes
                  .filter(d => d.type !== 'calculation')
                  .map(d => ({
                    value: d._id,
                    label: `${d.name} (${d.type})`
                  }))}
                value={selectedDataTypeForInsert}
                onChange={(value) => {
                  const selectedDataType = dataTypes.find(d => d._id === value)
                  if (selectedDataType) {
                    setFormula((formula ?? '') + '{{' + selectedDataType.name + '}}')
                    // Clear selection so it's ready for another insertion
                    setSelectedDataTypeForInsert('')
                  }
                }}
                placeholder='Insert a data type...'
                size='sm'
                maxW='400px'
                emptyMessage='No data types found'
              />}
            {saveDataType && formula && formula.length > 10 &&
              <ConfirmButton
                trigger={
                  <Button
                    size='xs'
                    variant='outline'
                    isLoading={generatingDescription}
                    disabled={generatingDescription}
                  >
                    <Icon as={LuSparkles} />
                    Describe and check my formula
                  </Button>
                }
                header='Save data type first?'
                body='You need to save the data type before we can generate a description. Would you like to save it now?'
                onConfirm={() => {
                  // Save first, then generate description
                  saveDataType(savedDataType => {
                    if (!savedDataType?.formula) return
                    setGeneratingDescription(true)
                    api.dataTypes.generateDescription(savedDataType._id, updatedDataType => {
                      setFormulaDescription(updatedDataType.formulaDescription || '')
                      setGeneratingDescription(false)
                      addDataType(updatedDataType)
                      utils.toast('success', 'Formula description generated')
                    }, err => {
                      utils.toast('error', `Failed to generate formula description: ${err.message}`)
                      setGeneratingDescription(false)
                    })
                  })
                }}
                actionColor='primary'
              />}
          </Stack>
          {formulaDescription &&
            <Stack gap={2} p={3} bg='bg.subtle' rounded='md' borderWidth='1px' borderColor='border.emphasized'>
              <Text fontWeight='bold' fontSize='sm'>Formula description:</Text>
              <Text fontSize='sm' whiteSpace='pre-wrap'>{formulaDescription}</Text>
              <Text fontSize='xs' color='fg.muted'>Remember that AI can make mistakes.</Text>
            </Stack>}
        </Card>}
    </>
  )
}
